import { effectiveAppointmentType } from './effectiveAppointmentType';
import { patientSurgeryDate } from './patientSurgeryDate';

function isPastDue(patient) {
  const surgeryDate = patientSurgeryDate(patient);
  if (!surgeryDate) return false;
  const today = new Date();
  const surgery = new Date(surgeryDate);
  today.setHours(0, 0, 0, 0);
  surgery.setHours(0, 0, 0, 0);
  return surgery < today;
}

function byDateAsc(a, b) {
  return new Date(patientSurgeryDate(a) || 0) - new Date(patientSurgeryDate(b) || 0);
}

function toEntry(patient, includeCharacteristics) {
  return {
    id: patient.id,
    includeCharacteristics,
    appointmentId: patient.appointment_id,
  };
}

function dedupeById(rows, includeCharacteristics) {
  const seen = new Set();
  const queue = [];
  for (const patient of rows) {
    if (!patient?.id || seen.has(patient.id)) continue;
    seen.add(patient.id);
    queue.push(toEntry(patient, includeCharacteristics));
  }
  return queue;
}

/** Joint Decisions Upcoming tab: not past, date ascending. */
export function buildJointDecisionsUpcomingQueue(patients) {
  const list = Array.isArray(patients) ? patients : [];
  const upcoming = list
    .filter((patient) => effectiveAppointmentType(patient) !== 'SURGERY')
    .filter((patient) => !isPastDue(patient))
    .sort(byDateAsc);
  return dedupeById(upcoming, true);
}

/** Med Management Upcoming tab: pending approval first, then no-action. */
export function buildMedManagementUpcomingQueue(patients) {
  const list = Array.isArray(patients) ? patients : [];
  const mm = list
    .filter((patient) => effectiveAppointmentType(patient) === 'SURGERY')
    .filter((patient) => !isPastDue(patient))
    .sort(byDateAsc);
  const pending = mm.filter(
    (patient) => patient.has_medication_actions === true && patient.approval_status === null,
  );
  const upcoming = mm.filter(
    (patient) =>
      patient.has_medication_actions === false ||
      (patient.has_medication_actions === true && patient.approval_status !== null),
  );
  return dedupeById([...pending, ...upcoming], false);
}

export function buildListPrefetchQueue(patients, listKind) {
  return listKind === 'mm'
    ? buildMedManagementUpcomingQueue(patients)
    : buildJointDecisionsUpcomingQueue(patients);
}

export function prefetchListKindForPatient(patient) {
  return effectiveAppointmentType(patient) === 'SURGERY' ? 'mm' : 'jd';
}

/**
 * Next `limit` queue entries after fromIndex that are not already started.
 */
export function selectLookaheadEntries(queue, fromIndex, limit, startedIds) {
  const next = [];
  const started = startedIds instanceof Set ? startedIds : new Set(startedIds || []);
  const cap = Math.max(0, limit || 0);
  for (let i = Math.max(0, fromIndex); i < queue.length && next.length < cap; i += 1) {
    const entry = queue[i];
    if (!entry?.id || started.has(entry.id)) continue;
    next.push(entry);
  }
  return next;
}

export function maxCoveredIndex(queue, startedIds, pendingEntries) {
  let max = -1;
  const mark = (id) => {
    const index = queue.findIndex((entry) => entry.id === id);
    if (index > max) max = index;
  };
  for (const id of startedIds || []) mark(id);
  for (const entry of pendingEntries || []) {
    if (entry?.id) mark(entry.id);
  }
  return max;
}
